import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { Environment, EnvironmentService } from '@galaxy/core';
import {
  AccountAccountStatus,
  AccountDates,
  Account as AccountProductDetails,
  AccountsService,
  AddonActivation,
  AddonActivationAddonActivationStatus,
  Blame,
  DeactivationType,
  ListAppAndAddonActivationStatusFilter,
  ListAppsAndAddonsActivationsStatusesForBusinessResponseAppsAndAddonsActivationStatusesInterface,
  PendingActivation,
  GetMultiRequestActivationIdentifier as SDKActivationIdentifier,
} from '@vendasta/accounts/legacy';
import { DomainService } from '@vendasta/domain';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MarketplaceAppService as MarketplacePackagesAppService } from '@vendasta/marketplace-packages';
import { BehaviorSubject, EMPTY, Observable, combineLatest, forkJoin, merge, of, throwError } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  first,
  map,
  publishReplay,
  refCount,
  scan,
  shareReplay,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AppEditionService, checkForEditionWithEmptyStringId } from '../activation/core/app-edition.service';
import { Edition, ProductEditionMap } from '../activation/core/interface';
import { LISTING_DISTRIBUTION_ADDON_IDS_DEMO, LISTING_DISTRIBUTION_ADDON_IDS_PROD } from '../constants';
import { Account } from './account';
import { FileGroup } from './filegroup';
import { FileGroupService } from './filegroup.service';
import { MarketplaceAppService } from './marketplace-app.service';
import { OrderFormSubmission, OrderFormSubmissionField } from './order-form-submission';
import { Salesperson } from './salesperson';
import { SalespersonService } from './salesperson.service';
import { TaxonomyService } from './taxonomy.service';
import { User } from './user';

export interface AccountDetails {
  account: Account;
  users?: User[];
  salesperson?: Salesperson;
  taxonomy?: string;
  fileGroups?: FileGroup[];
}

export interface Activation {
  businessId: string;
  appId: string;
  addonId?: string;
  activationId?: string;
  anniversaryDate: Date;
  status: AccountAccountStatus | AddonActivationAddonActivationStatus;
  trial?: boolean;
  commitmentDate: Date;
  activation: Date;
  deactivation?: Date;
  orderFormSubmissionId: string;
  isSuspended?: boolean;
  accountId?: string;
  activationDescriptor?: string;
  editionId?: string;
  deactivationIsEditionChange?: boolean;
  deactivationNewEditionId?: string;
  deactivationNewBillingOrderId?: string;
}

export interface ActivationIdentifier {
  accountGroupId: string;
  appId: string;
  activationId: string;
}

@Injectable({ providedIn: 'root' })
export class AccountService {
  private accountID$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _accountID$: Observable<string>;
  private accountDetails$$: BehaviorSubject<AccountDetails> = new BehaviorSubject<AccountDetails>(null);
  private users$$: BehaviorSubject<User[]> = new BehaviorSubject<User[]>(null);
  private _users$ = this.users$$.asObservable();
  private loadingUsers$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private _loadingUsers$ = this.loadingUsers$$.asObservable();

  activations$: Observable<Activation[]>;
  account$: Observable<Account>;
  accountMarketId$: Observable<string>;
  editionsMap$: Observable<ProductEditionMap>;
  private reloadProducts$$: BehaviorSubject<void> = new BehaviorSubject<void>(null);

  private _userMask: string[];

  constructor(
    private http: HttpClient,
    @Inject('PARTNER_ID') private partnerId$: Observable<string>,
    private salespersonService: SalespersonService,
    private taxonomyService: TaxonomyService,
    private accountsService: AccountsService,
    private fileGroupService: FileGroupService,
    private appService: MarketplacePackagesAppService,
    private environmentService: EnvironmentService,
    private editionsService: AppEditionService,
    private marketplaceAppService: MarketplaceAppService,
    private accountGroupService: AccountGroupService,
    protected readonly domainService: DomainService,
    private snackbarService: SnackbarService,
  ) {
    this._accountID$ = this.accountID$$.asObservable().pipe(skipWhile((id) => id === null));
    this._userMask = [];

    this.account$ = this.accountID$.pipe(
      skipWhile((account) => account === null),
      tap((accountId) => {
        if (this.accountDetails$$.getValue()?.account.account_group_id !== accountId) {
          this.accountDetails$$.next({ account: new Account({ account_group_id: accountId }) });
        }
      }),
      switchMap((accountId) => {
        return this.getAccount(accountId);
      }),
    );
    this.accountMarketId$ = this.account$.pipe(
      filter((account) => !!account),
      map((acc) => acc.market_id || 'default'),
    );

    this.account$.pipe(switchMap((account) => this.loadAccountDetails(account, this._userMask))).subscribe(
      (accountDetails) => {
        return this.accountDetails$$.next(accountDetails);
      },
      (err) => console.log(err),
    );

    const accountDates$: Observable<AccountDates[]> = combineLatest([this.accountID$, this.reloadProducts$$]).pipe(
      switchMap(([accountId, _]) => this.accountsService.listAccountDates(accountId, '', 1000)),
      catchError((err) => {
        console.log(err);
        return [];
      }),
      publishReplay(1),
      refCount(),
    );

    const appActivations$ = this.reloadProducts$$.pipe(
      switchMap(() => this.accountID$),
      withLatestFrom(this.partnerId$),
      switchMap(([accountId, partnerId]) => this.listActiveProducts(accountId, partnerId)),
      map((acc) => this.appActivationsToActivations(acc)),
      publishReplay(1),
      refCount(),
    );

    const addonActivations$ = this.reloadProducts$$.pipe(
      switchMap(() => {
        return combineLatest([appActivations$, this.accountID$]).pipe(
          map(([appActivations, accountID]) =>
            appActivations.map((act) => {
              return this.accountsService
                .listAddonActivations(accountID, act.appId)
                .pipe(map((resp) => resp.activations || []));
            }),
          ),
          switchMap(
            (responses: Observable<AddonActivation[]>[]): Observable<AddonActivation[]> =>
              forkJoin(...responses).pipe(
                // flatten the array of arrays
                map((arrays) => [].concat([], ...arrays)),
              ),
          ),
          map((act) => this.addonActivationsToActivations(act)),
          startWith([]),
        );
      }),
      publishReplay(1),
      refCount(),
    );

    this.editionsMap$ = this.reloadProducts$$.pipe(
      switchMap(() => {
        return combineLatest([appActivations$, this.marketplaceAppService.apps$]).pipe(
          map(([appActivations, apps]) => {
            const appIds = appActivations.map((appActivation) => appActivation.appId);
            return appIds.filter((appId) => apps.find((a) => a.appId === appId && a.usesEditions));
          }),
          switchMap((appIds) => {
            if (appIds && appIds.length > 0) {
              return this.editionsService.getEditionsForApps(appIds);
            }
            return of(new Map<string, Edition[]>() as ProductEditionMap);
          }),
        );
      }),
      publishReplay(1),
      refCount(),
    );

    this.activations$ = this.reloadProducts$$.pipe(
      switchMap(() => {
        return combineLatest([appActivations$, addonActivations$, accountDates$, this.editionsMap$]).pipe(
          map(([appActivations, addonActivations, accountDates, editionsMap]) => {
            const editionedAppActivations = appActivations.map((activation) =>
              this.addEmptyEditionIdsToActivationsIfAppUsesEditions(activation, editionsMap),
            );
            const activations = editionedAppActivations
              .concat(addonActivations)
              .sort(this.compareActivations)
              .map((act) => mergeActivationsAndDates(act, accountDates));
            return activations;
          }),
        );
      }),
      shareReplay(1),
    );
  }

  getAppStatusesForBusiness(
    businessId: string,
    filters?: ListAppAndAddonActivationStatusFilter,
  ): Observable<ListAppsAndAddonsActivationsStatusesForBusinessResponseAppsAndAddonsActivationStatusesInterface[]> {
    return this.accountsService
      .listAppsAndAddonsActivationStatusesForBusiness(businessId, filters)
      .pipe(map((statuses) => (statuses ? statuses : [])));
  }

  private loadAccountDetails(account: Account, mask?: string[]): Observable<AccountDetails | null> {
    if (!account) {
      return of(null);
    }
    return merge(
      this.taxonomyService.getTaxonomyFromTaxonomyId(account.tax_ids).pipe(
        map((t) => ({ taxonomy: t })),
        catchError(() => of({ taxonomy: '' })),
      ),
      this.salespersonService.getSalesperson(account.sales_person_id, account.partner_id).pipe(
        map((s) => ({ salesperson: s })),
        take(1),
        catchError(() => of({ salesperson: null })),
      ),
      this.loadUsers(account.account_group_id, account.partner_id, mask).pipe(
        tap((u) => this.users$$.next(u)),
        switchMap(() => EMPTY),
      ), // Deprecated [users] in accountDetails
      this.fileGroupService.getFileGroups(account.account_group_id, account.partner_id).pipe(
        map((f) => ({ fileGroups: f || [] })),
        catchError(() => of({ fileGroups: [] })),
      ),
    ).pipe(scan((acc: AccountDetails, cur: any) => ({ ...acc, ...cur }), { account }));
  }

  setCurrentAccountID(accountGroupId: string, refresh = true, userMask: string[] = []): void {
    if (refresh || this.accountID$$.getValue() !== accountGroupId) {
      this._userMask = userMask;
      this.accountID$$.next(accountGroupId);
    }
  }

  public get accountID$(): Observable<string> {
    return this._accountID$;
  }

  public get accountDetails$(): Observable<AccountDetails> {
    return combineLatest([this.accountDetails$$.asObservable().pipe(filter((acc) => acc !== null)), this.users$]).pipe(
      map(([accountDetails, users]) => ({
        ...accountDetails,
        users,
      })),
    );
  }

  public clearAccountDetails(): void {
    this.accountDetails$$.next(null);
  }

  public get users$(): Observable<User[]> {
    return this._users$;
  }

  public get loadingUsers$(): Observable<boolean> {
    return this._loadingUsers$;
  }

  public getAccount(accountGroupId: string): Observable<Account> {
    const accountGroup$ = this.accountGroupService
      .get(
        accountGroupId,
        new ProjectionFilter({
          napData: true,
          legacyProductDetails: true,
          accountGroupExternalIdentifiers: true,
          snapshotReports: true,
          additionalCompanyInfo: true,
        }),
      )
      .pipe(publishReplay(1), refCount());
    const sscDomain$ = accountGroup$.pipe(
      switchMap((accountGroup) => {
        if (accountGroup?.externalIdentifiers?.partnerId) {
          return this.domainService.getDomainByIdentifier(
            `/application/ST/partner/${accountGroup.externalIdentifiers.partnerId}`,
          );
        } else {
          return of(null);
        }
      }),
      map((resp) => {
        return resp?.primary?.domain;
      }),
      take(1),
      publishReplay(1),
      refCount(),
    );

    return combineLatest([accountGroup$, sscDomain$]).pipe(
      catchError(() => this.errorHandler('Cannot get account')),
      map(([result, domain]) => (result ? Account.AccountFromAccountGroup(result, domain) : null)),
    );
  }

  loadUsers(accountGroupId: string, partnerId: string, mask?: string[]): Observable<User[]> {
    this.loadingUsers$$.next(true);
    const url = '/_ajax/v1/users/';
    const opts = {
      params: {
        pid: partnerId,
        agid: accountGroupId,
        mask: [],
      },
    };
    if (mask !== undefined && mask !== null) {
      opts.params.mask = mask;
    }
    return this.http.get<{ data: any }>(url, opts).pipe(
      take(1),
      map((response) => response.data.map((userData) => new User(userData))),
      catchError(() => this.errorHandler('Cannot load users', [])),
      finalize(() => this.loadingUsers$$.next(false)),
    );
  }

  addUserToAccount(accountGroupId: string, user: User): void {
    const url = '/_ajax/v1/associate-user-with-account-group/';

    this.partnerId$
      .pipe(
        first(),
        map((partnerId) => ({ accountGroupId, partnerId, userId: user.userId })),
        switchMap((body) => this.http.post<{ data: any }>(url, body)),
        withLatestFrom(this.users$),
      )
      .subscribe(
        ([_, users]) => {
          this.users$$.next([...(users || []).filter((u) => u.userId !== user.userId), user]);
          this.snackbarService.openSuccessSnack('User Added');
        },
        () => this.snackbarService.openErrorSnack('Cannot add user to account'),
      );
  }

  addUsersToAccount(partnerId: string, accountGroupId: string, usersToAdd: User[]): Observable<unknown> {
    const url = '/_ajax/v1/associate-user-with-account-group/';
    const requests = usersToAdd.map((user) => this.http.post(url, { accountGroupId, partnerId, userId: user.userId }));

    return forkJoin(requests).pipe(
      tap(() => {
        usersToAdd.map((user) =>
          this.users$$.next([...(this.users$$.value || []).filter((u) => u.userId !== user.userId), user]),
        );
        this.snackbarService.openSuccessSnack(
          `${usersToAdd.length} user${usersToAdd.length > 1 ? 's' : ''} added to account.`,
        );
      }),
      catchError(() => {
        this.snackbarService.openErrorSnack('Something went wrong when adding users. Refresh the page and try again.');
        return of({});
      }),
    );
  }

  getUserAssociationsForDeletion(accountGroupId: string): Observable<any> {
    return this.http
      .get<{ data: any }>('/_ajax/v1/get-user-associations-for-deletion/', {
        params: { accountGroupId: accountGroupId },
      })
      .pipe(
        map((res) => res.data),
        take(1),
      );
  }

  removeUserFromAccount(accountGroupId: string, userId: string): void {
    const url = '/_ajax/v1/delete-user-association/?accountGroupId=' + accountGroupId + '&userId=' + userId;
    this.http
      .get<{ data: any }>(url)
      .pipe(withLatestFrom(this.users$))
      .subscribe(
        ([_, users]) => {
          this.snackbarService.openSuccessSnack('User Removed');
          this.users$$.next(users.filter((user) => user.userId !== userId));
        },
        () => this.snackbarService.openErrorSnack('Could not remove user. Please try again'),
      );
  }

  removeAccountFromAccountGroup(
    accountGroupId: string,
    accountId: string,
    productId: string,
    productName: string,
  ): Observable<any> {
    const url = '/_ajax/v1/remove-account/';
    const body = { agid: accountGroupId, account_id: accountId, product_id: productId, product_name: productName };
    return this.http.post(url, body);
  }

  cancelAddon(
    businessId: string,
    appId: string,
    addonId: string,
    activationId: string,
    partnerId: string,
    deactivationInfo: Blame,
  ): Observable<boolean> {
    return this.accountsService.deactivateAddon(businessId, appId, addonId, activationId, partnerId, deactivationInfo);
  }

  immediatelyDeactivateAddon(
    businessId: string,
    appId: string,
    addonId: string,
    activationId: string,
    partnerId: string,
    deactivationInfo: Blame,
  ): Observable<boolean> {
    return this.accountsService.deactivateAddon(
      businessId,
      appId,
      addonId,
      activationId,
      partnerId,
      deactivationInfo,
      DeactivationType.DEACTIVATION_TYPE_IMMEDIATE,
    );
  }

  undoCancel(appId: string, businessId: string, activationId: string, undoInfo: Blame): Observable<boolean> {
    return this.accountsService.UndoCancel(appId, businessId, activationId, undoInfo);
  }

  //XXX for obvious reasons, get rid of this, make getOrderFormSubmission return what we need, and make sure we don't break existing users
  getFullOrderFormSubmission(productId: string, orderFormSubmissionId: string): Observable<any> {
    //XXX need to get the right actually complete object type that contains the full result data
    const url = '/_ajax/v1/account/order-form-submission/';
    const params = {
      partnerId: null, //the api already infers this
      accountGroupId: null, //the api already infers this XXX: perhaps the api should be updated, or another thin wrapper made in the backend
      productId: productId,
      orderFormSubmissionId: orderFormSubmissionId,
    };

    return this.http.get<{ data: any }>(url, { params }).pipe(
      map(({ data }) => {
        data.customFields.forEach((element) => {
          if (element.field_type === 'text') {
            Object.keys(element).forEach((key) => {
              element[key] = element[key] ? element[key].toString() : '';
            });
          }
        });
        return data;
      }),
    );
  }

  getOrderFormSubmission(
    partnerId: string,
    accountGroupId: string,
    productId: string,
    orderFormSubmissionId?: string,
  ): Observable<OrderFormSubmission> {
    if (
      !orderFormSubmissionId &&
      !productId.toUpperCase().startsWith('MP-') &&
      !productId.toUpperCase().startsWith('A-')
    ) {
      return of(
        new OrderFormSubmission({
          commonFields: [],
          customFields: [],
        }),
      );
    }
    const url = '/_ajax/v1/account/order-form-submission/';
    const params = {
      partnerId: partnerId,
      accountGroupId: accountGroupId,
      productId: productId,
      orderFormSubmissionId: orderFormSubmissionId || '',
    };

    return this.http
      .get<{
        data: {
          common_fields: OrderFormSubmissionField;
          custom_fields: OrderFormSubmissionField;
        };
      }>(url, {
        params: params,
      })
      .pipe(
        map((res) => res.data),
        map(
          (data) =>
            new OrderFormSubmission({
              commonFields: data.common_fields || [],
              customFields: data.custom_fields || [],
            }),
        ),
      );
  }

  deactivate(businessId: string, appId: string, deactivationInfo: Blame, activationId: string): Observable<boolean> {
    return this.accountsService.deactivate(
      businessId,
      appId,
      null,
      null,
      deactivationInfo,
      DeactivationType.DEACTIVATION_TYPE_CANCEL,
      activationId,
    );
  }

  listActiveProducts(
    businessId: string,
    partnerId: string,
    cursor?: string,
    pageSize?: number,
  ): Observable<AccountProductDetails[]> {
    return this.accountsService
      .list(businessId, partnerId, cursor, pageSize)
      .pipe(map((listResponse) => listResponse.accounts));
  }

  private errorHandler(errmsg: string, errorValue: any = null, shouldThrowError = false): Observable<any> {
    this.snackbarService.openErrorSnack(errmsg);
    return shouldThrowError ? throwError(errmsg) : of(errorValue);
  }

  listActivationsForBusiness(businessId: string, partnerId: string): Observable<Activation[]> {
    const accountDates$ = this.accountsService.listAccountDates(businessId, '', 1000);

    const appActivations$ = this.listActiveProducts(businessId, partnerId).pipe(
      map((acc) => this.appActivationsToActivations(acc)),
    );

    const addonActivations$ = appActivations$.pipe(
      map((appActivations) =>
        appActivations.map((act) => {
          return this.accountsService
            .listAddonActivations(businessId, act.appId)
            .pipe(map((resp) => resp.activations || []));
        }),
      ),
      switchMap((responses: Observable<AddonActivation[]>[]): Observable<AddonActivation[]> => {
        if (responses.length === 0) {
          return of([]);
        }
        return forkJoin(...responses).pipe(
          // flatten the array of arrays
          map((arrays) => [].concat([], ...arrays)),
        );
      }),
      map((act) => this.addonActivationsToActivations(act)),
    );

    return combineLatest([appActivations$, addonActivations$, accountDates$]).pipe(
      map(([appActivations, addonActivations, accountDates]) => {
        const activations = appActivations
          .concat(addonActivations)
          .sort(this.compareActivations)
          .map((act) => mergeActivationsAndDates(act, accountDates));
        return activations;
      }),
    );
  }

  listPendingActivationsForBusiness(businessId: string): Observable<PendingActivation[]> {
    return this.accountsService.listPendingActivationsForBusiness(businessId, '', 1000).pipe(
      map((listResponse) => listResponse.pendingActivations || []),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  dismissPendingActivation(pending: {
    businessId: string;
    appId: string;
    addonId?: string;
    activationId: string;
  }): Observable<boolean> {
    return this.accountsService.dismissPendingActivation(
      pending.activationId,
      null,
      pending.businessId,
      pending.appId,
      pending.addonId,
    );
  }

  /*
   * Check if this account is being served with the old non-marketplace LD, or marketplace LD.
   * */
  // TODO: move this off of SMB-account-specific class?
  isUsingMarketplaceLD(): Observable<boolean> {
    let listingDistributionAddonIds = [];
    switch (this.environmentService.getEnvironment()) {
      case Environment.LOCAL:
      case Environment.DEMO:
        listingDistributionAddonIds = [...LISTING_DISTRIBUTION_ADDON_IDS_DEMO];
        break;
      case Environment.PROD:
        listingDistributionAddonIds = [...LISTING_DISTRIBUTION_ADDON_IDS_PROD];
        break;
    }

    return this.partnerId$.pipe(
      take(1),
      switchMap((partnerId) => this.appService.areAppsDistributedToPartner(partnerId, listingDistributionAddonIds)),
      map((appDistribution) => appDistribution.some((ad) => ad.isDistributed === true)),
      shareReplay(1),
    );
  }

  reloadProducts(): void {
    this.reloadProducts$$.next(null);
  }

  private compareActivations(a: Activation, b: Activation): number {
    if (a.appId < b.appId) {
      return -1;
    }
    if (a.appId > b.appId) {
      return 1;
    }
    // Handle No Addon ID
    if (!a.addonId && !!b.addonId) {
      return -1;
    }
    if (!!a.addonId && !b.addonId) {
      return 1;
    }

    if (a.addonId < b.addonId) {
      return -1;
    }
    if (a.addonId > b.addonId) {
      return 1;
    }
    if (a.activation < b.activation) {
      return -1;
    }
    if (a.activation > b.activation) {
      return 1;
    }
    return 0;
  }

  private appActivationsToActivations(accounts: AccountProductDetails[]): Activation[] {
    if (!accounts) {
      return [];
    }
    return accounts.map((acc) => ({ ...acc, appId: this.getAppId(acc) }));
  }

  private getAppId(account: AccountProductDetails): string {
    if (account.productId === 'MP') {
      return account.appId;
    } else if (account.productId === 'CP') {
      return account.accountId;
    } else {
      return account.productId;
    }
  }

  private addonActivationsToActivations(activations: AddonActivation[]): Activation[] {
    if (!activations) {
      return [];
    }
    const now = new Date();
    return activations
      .filter((act) => !act.deactivated || act.deactivated > now)
      .map((a) => ({ ...a, activation: a.activated, deactivation: a.deactivated }));
  }

  getMultiActivations(activationIdentifiers: ActivationIdentifier[]): Observable<AccountProductDetails[]> {
    const req: SDKActivationIdentifier[] = [];
    for (const activationIdentifier of activationIdentifiers) {
      req.push(
        new SDKActivationIdentifier({
          businessId: activationIdentifier.accountGroupId,
          appId: activationIdentifier.appId,
          activationId: activationIdentifier.activationId,
        }),
      );
    }
    return this.accountsService.getMulti(req);
  }

  addEmptyEditionIdsToActivationsIfAppUsesEditions(activation: Activation, editionsMap: ProductEditionMap): Activation {
    const { appId } = activation;
    const editionsEntry = editionsMap.get(appId);

    if (activation.editionId) {
      if (activation.deactivationIsEditionChange) {
        if (!activation.deactivationNewEditionId && editionsEntry && checkForEditionWithEmptyStringId(editionsEntry)) {
          activation.deactivationNewEditionId = '';
        }
      }
    } else if (editionsEntry && checkForEditionWithEmptyStringId(editionsEntry)) {
      activation.editionId = '';
    }

    return activation;
  }
}

export function mergeActivationsAndDates(act: Activation, accountDates: AccountDates[]): Activation {
  if (!accountDates || accountDates.length < 1) {
    return act;
  }
  const date =
    accountDates.find((item) => item.activationId === act.activationId) ||
    accountDates.find((item) => item.appId === act.appId && item.addonId === act.addonId);
  if (date) {
    act.anniversaryDate = date.anniversaryDate;
    act.commitmentDate = date.commitmentDate;
    if (!act.deactivation || act.deactivation.getFullYear() <= 1900) {
      act.deactivation = date.pendingDeactivation;
    }
  }
  return act;
}
